import { clinicianChartNotesOptions, type Pagination as PaginationMeta } from '@cartwheel/api/v1'
import {
  DataTable,
  EmptyState,
  FilterBar,
  LoadingView,
  Pagination,
  TextInput,
} from '@cartwheel/components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { type ClinicianChartNote, ClinicianChartNoteRow } from './clinician-chart-note-row'

interface Props {
  clinicianId: string
}

type SortDirection = 'asc' | 'desc'
type SortKey = 'created_at' | 'name' | 'patient_name' | 'score'

const DEFAULT_PER_PAGE = 10

export default function ClinicianChartNotesList({ clinicianId }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  const { data, isLoading, isFetching, error } = useQuery({
    ...clinicianChartNotesOptions({
      path: { clinician_id: clinicianId },
      query: { page: String(page), per_page: String(perPage) },
    }),
    placeholderData: keepPreviousData,
  })

  const chartNotes: ClinicianChartNote[] =
    (data?.chart_notes as ClinicianChartNote[] | undefined) ?? []

  const paginationMeta: PaginationMeta | undefined = data?.pagination

  const filteredNotes = useMemo(() => {
    if (!search.trim()) return chartNotes
    const q = search.toLowerCase()
    return chartNotes.filter(
      (n) => n.note_type?.toLowerCase().includes(q) || n.patient_name?.toLowerCase().includes(q)
    )
  }, [chartNotes, search])

  const sortedChartNotes = useMemo(() => {
    const valueForSort = (note: ClinicianChartNote, key: SortKey): number | string => {
      switch (key) {
        case 'created_at':
          return note.created_at ? Date.parse(note.created_at) : 0
        case 'name':
          return note.note_type ?? note.name ?? ''
        case 'patient_name':
          return note.patient_name ?? ''
        case 'score':
          return note.latest_clinical_quality_score?.percentage ?? -1
      }
    }

    const sorted = [...filteredNotes].sort((left, right) => {
      const leftValue = valueForSort(left, sortKey)
      const rightValue = valueForSort(right, sortKey)

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return leftValue - rightValue
      }

      return String(leftValue).localeCompare(String(rightValue))
    })

    return sortDirection === 'asc' ? sorted : sorted.reverse()
  }, [filteredNotes, sortDirection, sortKey])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSortKey(key)
    setSortDirection('asc')
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage)
    setPage(1)
  }

  const totalCount: number = paginationMeta?.total_count ?? chartNotes.length

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingView title="Loading chart notes..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-orange">Error loading chart notes</div>
      </div>
    )
  }

  return (
    <div className="px-4 py-10 lg:px-8 flex-1 overflow-auto">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-ginto-nord-md text-2xl text-charcoal-75">Chart Notes</h2>
      </div>

      <FilterBar
        rightSlot={
          <div className="relative">
            <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-50 text-sm pointer-events-none" />
            <TextInput
              placeholder="Search by note type or patient"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9 w-72"
            />
          </div>
        }
      >
        <span className="text-sm text-charcoal-50">
          {totalCount} chart note{totalCount !== 1 ? 's' : ''}
        </span>
      </FilterBar>

      {chartNotes.length === 0 ? (
        <EmptyState
          title="No chart notes found"
          description="Chart notes will appear here once sessions have been documented in Healthie."
        />
      ) : sortedChartNotes.length === 0 ? (
        <EmptyState title="No results" description={`No chart notes match "${search}".`} />
      ) : (
        <>
          <div
            className={`w-full border border-gray-stroke rounded-lg bg-white overflow-hidden transition-opacity duration-150 ${isFetching ? 'opacity-50' : ''}`}
          >
            <DataTable.Root className="min-w-full divide-y divide-gray-stroke">
              <DataTable.Header>
                <DataTable.Row>
                  <DataTable.HeaderCell
                    sortable
                    sortDirection={sortKey === 'created_at' ? sortDirection : null}
                    onSort={() => handleSort('created_at')}
                  >
                    Date
                  </DataTable.HeaderCell>
                  <DataTable.HeaderCell
                    sortable
                    sortDirection={sortKey === 'name' ? sortDirection : null}
                    onSort={() => handleSort('name')}
                  >
                    Chart Note
                  </DataTable.HeaderCell>
                  <DataTable.HeaderCell
                    sortable
                    sortDirection={sortKey === 'patient_name' ? sortDirection : null}
                    onSort={() => handleSort('patient_name')}
                  >
                    Patient
                  </DataTable.HeaderCell>
                  <DataTable.HeaderCell
                    sortable
                    sortDirection={sortKey === 'score' ? sortDirection : null}
                    onSort={() => handleSort('score')}
                  >
                    Quality Score
                  </DataTable.HeaderCell>
                  <DataTable.HeaderCell />
                  <DataTable.HeaderCell />
                </DataTable.Row>
              </DataTable.Header>
              <DataTable.Body className="bg-white divide-y divide-gray-stroke">
                {sortedChartNotes.map((note) => (
                  <ClinicianChartNoteRow key={note.id} chartNote={note} />
                ))}
              </DataTable.Body>
            </DataTable.Root>
          </div>

          {paginationMeta && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                itemsPerPage={perPage}
                totalPages={paginationMeta.total_pages}
                totalCount={paginationMeta.total_count}
                from={paginationMeta.from}
                to={paginationMeta.to}
                hasNextPage={paginationMeta.has_next_page}
                hasPrevPage={paginationMeta.has_prev_page}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handlePerPageChange}
                itemsPerPageOptions={[10, 25, 50, 100]}
                disabled={isFetching}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
